import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Tag, Plus, Trash2, RefreshCw, Edit2, Check, X,
  Percent, Euro, Mail, Star, Gift, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { formatDate, formatDateTime } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminPromo() {
  const [codes, setCodes] = useState([]);
  const [captures, setCaptures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('codes');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    code: '', description: '', type: 'pourcentage', valeur: 10,
    premiere_commande_only: false, utilisations_max: '', date_expiration: '', actif: true,
  });

  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [codesRes, capsRes] = await Promise.all([
        fetch(`${BACKEND}/api/admin/promo/codes`, { headers: H }).then(r => r.json()),
        fetch(`${BACKEND}/api/admin/promo/captures`, { headers: H }).then(r => r.json()),
      ]);
      setCodes(codesRes.codes || []);
      setCaptures(capsRes.captures || []);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  async function submit() {
    try {
      const body = {
        ...form,
        valeur: parseFloat(form.valeur) || 0,
        utilisations_max: form.utilisations_max ? parseInt(form.utilisations_max) : null,
        date_expiration: form.date_expiration || null,
      };
      const url = editId ? `${BACKEND}/api/admin/promo/codes/${editId}` : `${BACKEND}/api/admin/promo/codes`;
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: H, body: JSON.stringify(body) });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Erreur'); }
      toast.success(editId ? 'Code mis à jour' : 'Code créé');
      setShowForm(false); setEditId(null);
      setForm({ code: '', description: '', type: 'pourcentage', valeur: 10, premiere_commande_only: false, utilisations_max: '', date_expiration: '', actif: true });
      load();
    } catch (err) { toast.error(err.message); }
  }

  async function toggleActif(code) {
    await fetch(`${BACKEND}/api/admin/promo/codes/${code.id}`, {
      method: 'PUT', headers: H, body: JSON.stringify({ actif: !code.actif })
    });
    load();
  }

  async function deleteCode(id) {
    if (!window.confirm('Supprimer ce code ?')) return;
    await fetch(`${BACKEND}/api/admin/promo/codes/${id}`, { method: 'DELETE', headers: H });
    toast.success('Supprimé'); load();
  }

  function startEdit(code) {
    setForm({
      code: code.code, description: code.description || '',
      type: code.type, valeur: code.valeur,
      premiere_commande_only: code.premiere_commande_only || false,
      utilisations_max: code.utilisations_max || '',
      date_expiration: code.date_expiration?.slice(0,10) || '',
      actif: code.actif,
    });
    setEditId(code.id);
    setShowForm(true);
  }

  const TABS = [
    { id: 'codes', label: `Codes promo (${codes.length})`, icon: <Tag size={14}/> },
    { id: 'captures', label: `Emails popup (${captures.length})`, icon: <Mail size={14}/> },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }} data-testid="admin-promo">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Parametres</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Codes promo</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Codes de reduction</p>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="adm-title"  style={{ color: 'var(--admin-gold)' }}>Codes promo & Offres</h1>
          <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Gérez les codes de réduction et les emails capturés</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm" style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}>
            <RefreshCw size={14} className="mr-1"/> Actualiser
          </Button>
          {activeTab === 'codes' && (
            <Button onClick={() => { setShowForm(true); setEditId(null); }} size="sm"
              style={{ background: 'rgba(212,175,55,0.2)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.3)' }}>
              <Plus size={14} className="mr-1"/> Créer un code
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'rgba(0,0,0,0.3)' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={activeTab === tab.id
              ? { background: '#fff', color: 'var(--admin-teal)' }
              : { color: 'var(--admin-muted)' }}>
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {/* Formulaire création/édition */}
      {showForm && (
        <div className="p-5 rounded-2xl space-y-4" style={{ background: 'var(--admin-surface)', border: '1px solid rgba(212,175,55,0.2)' }}>
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-sm" style={{ color: 'var(--admin-gold)' }}>{editId ? 'Modifier' : 'Nouveau'} code promo</h2>
            <button onClick={() => setShowForm(false)} style={{ color: 'var(--admin-muted)' }}><X size={16}/></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Code (ex: SHOW10)</label>
              <Input value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                placeholder="SHOW10" style={{ background: 'var(--admin-surface2)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Description</label>
              <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Code bienvenue -10%" style={{ background: 'var(--admin-surface2)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Type de remise</label>
              <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl text-sm" style={{ background: 'var(--admin-surface2)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)' }}>
                <option value="pourcentage">Pourcentage (%)</option>
                <option value="montant">Montant fixe (€)</option>
              </select>
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>
                Valeur ({form.type === 'pourcentage' ? '%' : '€'})
              </label>
              <Input type="number" min="0" value={form.valeur} onChange={e => setForm(p => ({ ...p, valeur: e.target.value }))}
                style={{ background: 'var(--admin-surface2)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Nb utilisations max (vide = illimité)</label>
              <Input type="number" min="1" value={form.utilisations_max} onChange={e => setForm(p => ({ ...p, utilisations_max: e.target.value }))}
                placeholder="∞" style={{ background: 'var(--admin-surface2)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Date d'expiration</label>
              <Input type="date" value={form.date_expiration} onChange={e => setForm(p => ({ ...p, date_expiration: e.target.value }))}
                style={{ background: 'var(--admin-surface2)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
            </div>
          </div>
          <div className="flex gap-4 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.premiere_commande_only}
                onChange={e => setForm(p => ({ ...p, premiere_commande_only: e.target.checked }))} />
              <span className="text-xs" style={{ color: 'var(--admin-text)' }}>Première commande uniquement</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.actif}
                onChange={e => setForm(p => ({ ...p, actif: e.target.checked }))} />
              <span className="text-xs" style={{ color: 'var(--admin-text)' }}>Actif</span>
            </label>
          </div>
          <div className="flex gap-2">
            <Button onClick={submit} size="sm" style={{ background: 'rgba(212,175,55,0.2)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.3)' }}>
              <Check size={13} className="mr-1"/> Sauvegarder
            </Button>
            <Button onClick={() => setShowForm(false)} variant="outline" size="sm" style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-muted)' }}>
              Annuler
            </Button>
          </div>
        </div>
      )}

      {/* Liste codes */}
      {activeTab === 'codes' && (
        <div className="space-y-2">
          {loading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-16 rounded-xl" style={{ background: 'var(--admin-surface)' }} />)}</div>
          ) : codes.length === 0 ? (
            <div className="text-center py-12" style={{ color: 'var(--admin-faint)' }}>
              <Tag size={36} className="mx-auto mb-3 opacity-20"/>
              <p>Aucun code promo créé</p>
            </div>
          ) : codes.map(code => (
            <div key={code.id} className="flex items-center gap-4 p-4 rounded-xl"
              style={{ background: 'var(--admin-surface)', border: `1px solid ${code.actif ? 'rgba(31,157,122,0.2)' : 'rgba(255,255,255,0.06)'}`, opacity: code.actif ? 1 : 0.5 }}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-mono font-bold text-sm" style={{ color: 'var(--admin-gold)' }}>{code.code}</p>
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold"
                    style={{ background: code.type === 'pourcentage' ? 'rgba(93,188,191,0.12)' : 'rgba(212,175,55,0.12)', color: code.type === 'pourcentage' ? '#5DBCBF' : '#D4AF37' }}>
                    {code.type === 'pourcentage' ? <><Percent size={10} className="inline"/> {code.valeur}%</> : <><Euro size={10} className="inline"/> {code.valeur}€</>}
                  </span>
                  {code.premiere_commande_only && (
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(139,92,246,0.12)', color: 'var(--admin-purple)' }}>1ère cmd</span>
                  )}
                  {code.actif
                    ? <span className="text-xs" style={{ color: 'var(--admin-green)' }}>● Actif</span>
                    : <span className="text-xs" style={{ color: 'var(--admin-red)' }}>○ Inactif</span>}
                </div>
                <p className="text-xs mt-0.5" style={{ color: 'var(--admin-faint)' }}>
                  {code.description || 'Sans description'}
                  {code.utilisations_max && ` · ${code.utilisations_count || 0}/${code.utilisations_max} utilisations`}
                  {code.date_expiration && ` · Expire ${formatDate(code.date_expiration)}`}
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => toggleActif(code)} className="p-1.5 rounded-lg"
                  style={{ color: code.actif ? '#1F9D7A' : '#e53e3e', background: code.actif ? 'rgba(31,157,122,0.1)' : 'rgba(229,62,62,0.1)' }}>
                  {code.actif ? <Check size={13}/> : <X size={13}/>}
                </button>
                <button onClick={() => startEdit(code)} className="p-1.5 rounded-lg" style={{ color: 'var(--admin-blue)', background: 'var(--admin-blue-dim)' }}>
                  <Edit2 size={13}/>
                </button>
                <button onClick={() => deleteCode(code.id)} className="p-1.5 rounded-lg" style={{ color: 'var(--admin-red)', background: 'var(--admin-red-dim)' }}>
                  <Trash2 size={13}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Emails capturés */}
      {activeTab === 'captures' && (
        <div>
          <p className="text-xs mb-4" style={{ color: 'var(--admin-muted)' }}>
            {captures.length} email{captures.length > 1 ? 's' : ''} capturé{captures.length > 1 ? 's' : ''} via le popup -5%
          </p>
          <div className="space-y-2">
            {loading ? (
              <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12 rounded-xl" style={{ background: 'var(--admin-surface)' }} />)}</div>
            ) : captures.map(cap => (
              <div key={cap.id} className="flex items-center justify-between px-4 py-3 rounded-xl"
                style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--admin-text)' }}>{cap.email}</p>
                  <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>
                    {formatDateTime(cap.captured_at)}
                  </p>
                </div>
                <span className="font-mono text-xs font-bold" style={{ color: 'var(--admin-gold)' }}>{cap.promo_code}</span>
              </div>
            ))}
            {captures.length === 0 && (
              <div className="text-center py-12" style={{ color: 'var(--admin-faint)' }}>
                <Mail size={36} className="mx-auto mb-3 opacity-20"/>
                <p>Aucun email capturé pour le moment</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
